import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DynamicForm, SelectOption } from '../../../components/form-builder/form-builder.types';
import { BankDetailsService } from './bank-details.service';
import { RefParamsService } from '../ref-params/ref-params.service';
import { BankDetails, RefTypeEnum } from '@/types';
import { DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { DrawerComponent } from '../../../components/drawer/drawer.component';
import { DrawerObject } from '../../../components/drawer/drawer.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { CompanyService } from '../company/company.service';
import { MessageService } from 'primeng/api';
import { getFormBuilderObjectFactory } from '../../../components/form-builder/utils/form-builder-factory';
import { BankDetailsDto } from '@/types/dtos.types';
import { BankDetailsRepository } from '../../../store/bank-details-state/bank-details-state.repository';
import { getBankDetailsFormStructure } from './utils/bank-details-form-object';
import { getBankDetailsAddDrawerObject } from './utils/bank-details-add-drawer-object';
import { getBankDetailsUpdateDrawerObject } from './utils/bank-details-update-drawer-object copy';
import { getBankDetailsDeleteObject } from './utils/bank-details-delete-dialog-object';
import { getBankDetailsDataTableStructure } from './utils/bank-details-data-table-object';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'sm-bank-details',
  templateUrl: './bank-details.component.html',
  styleUrl: './bank-details.component.scss',
  imports: [
    PageHeaderComponent,
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DrawerComponent,
    DataTableBuilderComponent,
    DialogComponent,
    TranslateModule,
  ],
})
export class BankDetailsComponent implements OnInit {
  //services
  refParamsService = inject(RefParamsService);
  bankDetailsService = inject(BankDetailsService);
  bankDetailsRepository = inject(BankDetailsRepository);
  companyService = inject(CompanyService);
  messageService = inject(MessageService);
  translateService = inject(TranslateService);

  //inputs
  @Input() companyId?: number;

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.bankDetails.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.bankDetails.breadcrumb.bankDetails',
      routerLink: ['/settings', 'bank-details'],
    },
  ];

  //drawer
  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  bankDetailsDrawer?: DrawerObject;

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  bankDetailsDialog?: DialogObject;

  //form-object
  formObject: DynamicForm = getFormBuilderObjectFactory();
  bankDetailsCountryOptions: SelectOption[] = [];

  // data-table
  // row-actions
  rowActions?: DataTableRowActions;
  //data
  data: BankDetails[] = [];
  //data-table-object
  dataTableObject?: DynamicDataTable;
  loading = false;

  ngOnInit(): void {
    this.loadData();
    this.refParamsService.getSpecializedRefParams(RefTypeEnum.COUNTRY).subscribe(data => {
      this.bankDetailsCountryOptions = data.map(refParams => {
        return { name: refParams.description, code: refParams.id };
      });
    });
    this.rowActions = {
      editAction: () => {
        this.formObject = getBankDetailsFormStructure({
          store: this.bankDetailsRepository,
          bankDetailsCountryOptions: this.bankDetailsCountryOptions,
        });
        this.bankDetailsDrawer = getBankDetailsUpdateDrawerObject({
          store: this.bankDetailsRepository,
          formObject: this.formObject,
          action: () =>
            this.updateBankDetails(this.bankDetailsRepository.getNested('response', 'id'), {
              ...this.bankDetailsRepository.getNested('dto'),
              societeId: this.companyId!,
            }),
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
        });
        this.drawerComponent.toggleVisible();
      },
      deleteAction: () => {
        this.bankDetailsDialog = getBankDetailsDeleteObject({
          t: this.translateService,
          store: this.bankDetailsRepository,
          representation: this.bankDetailsRepository.getNested('response', 'id'),
          action: () => {
            this.deleteBankDetails(this.bankDetailsRepository.getNested('response', 'id'));
          },
          toggleVisiblity: () => this.dialogComponent.toggleVisible(),
        });
        this.dialogComponent.toggleVisible();
      },
    };
    this.dataTableObject = getBankDetailsDataTableStructure({
      store: this.bankDetailsRepository,
      createAction: () => {
        this.formObject = getBankDetailsFormStructure({
          store: this.bankDetailsRepository,
          bankDetailsCountryOptions: this.bankDetailsCountryOptions,
        });
        this.bankDetailsDrawer = getBankDetailsAddDrawerObject({
          store: this.bankDetailsRepository,
          formObject: this.formObject,
          action: () => this.addBankDetails(this.bankDetailsRepository.getNested('dto')),
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
        });
        this.drawerComponent.toggleVisible();
      },
      rowActions: this.rowActions,
    });
  }

  loadData() {
    this.loading = true;
    if (this.companyId) {
      this.companyService.getCompanyBankDetails(this.companyId).subscribe(data => {
        this.data = data.content;
        this.loading = false;
      });
    } else {
      this.bankDetailsService.getBankDetails().subscribe(data => {
        this.data = data.content;
        this.loading = false;
      });
    }
  }

  addBankDetails(bankDetails: BankDetailsDto) {
    const next = () => {
      this.drawerComponent.toggleVisible();
      this.loadData();
      this.loading = false;
      this.bankDetailsRepository.reset();
      this.messageService.add({
        severity: 'success',
        summary: 'Adresse ajouté',
        detail: 'Le détail du compte bancaire a été ajouté avec succès.',
      });
    };

    const error = () => {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: "Une erreur est survenue lors de l'ajout des détails du compte bancaire.",
      });
    };

    if (this.companyId) {
      this.companyService.addCompanyBankDetails(this.companyId, bankDetails).subscribe({
        next,
        error,
      });
    } else {
      this.bankDetailsService.addBankDetails(bankDetails).subscribe({
        next,
        error,
      });
    }
  }

  updateBankDetails(id: number, bankDetails: BankDetailsDto) {
    this.bankDetailsService.updateBankDetails(id, bankDetails).subscribe({
      next: () => {
        this.drawerComponent.toggleVisible();
        this.loadData();
        this.loading = false;
        this.bankDetailsRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Détail du compte bancaire modifié',
          detail: 'Les détails du compte bancaire ont été modifiés avec succès.',
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors de la modification des détails du compte bancaire.',
        });
      },
    });
  }

  deleteBankDetails(id: number) {
    this.bankDetailsService.deleteBankDetails(id).subscribe({
      next: () => {
        this.dialogComponent.toggleVisible();
        this.loadData();
        this.loading = false;
        this.bankDetailsRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: ' supprimé',
          detail: 'Les détail du compte bancaire a été supprimé avec succès.',
        });
      },
      error: () => {
        this.loading = false;
        this.bankDetailsRepository.reset();
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors de la suppression des détails du compte bancaire.',
        });
      },
    });
  }
}
